import { Controller } from 'stimulus';
import gsap from 'gsap';

class NavigationController extends Controller {
  connect() {
    this.isExpanded = false;
  }

  onNavigationToggle() {
    this.toggleNavigation();
    this.closeDropdowns();
    this.toggleLanguageDropdown(false);
  }

  // eslint-disable-next-line class-methods-use-this
  onDropdownToggle(event) {
    const toggle = event.currentTarget;

    // Get the open state before closing everything down
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

    // Close all dropdowns, this can include the current dropdown
    this.closeDropdowns();

    // If the dropdown was already open, don't open it again
    if (isExpanded) {
      return;
    }

    const dropdown = document.querySelector(`[aria-controlledby="${toggle.id}"]`);

    toggle.setAttribute('aria-expanded', 'true');

    dropdown.classList.add('is-visible');
  }

  onLanguageToggle() {
    this.toggleNavigation({ forceIsExpanded: false });
    this.closeDropdowns();
    this.toggleLanguageDropdown();
  }

  toggleNavigation({ forceIsExpanded = null } = {}) {
    const isExpanded = this.navigationToggleTarget.getAttribute('aria-expanded') === 'true';

    this.isExpanded = forceIsExpanded !== null ? forceIsExpanded : !isExpanded;

    this.navigationToggleTarget.setAttribute('aria-expanded', this.isExpanded);
    this.navigationTarget.setAttribute('aria-expanded', this.isExpanded);

    const isExpandedStateChanged = this.isExpanded !== isExpanded;

    if (this.isExpanded) {
      // Only change the focus if the expanded state changed. Otherwise it would
      // refocus on every window click and some keys (like escape).
      if (isExpandedStateChanged) {
        this.navigationTarget.focus();
      }

      this.openNavigationDropdown();
    } else {
      // Only change the focus if the expanded state changed. Otherwise it would
      // refocus on every window click and some keys (like escape).
      if (isExpandedStateChanged) {
        this.navigationToggleTarget.focus();
      }

      this.closeNavigationDropdown();
    }
  }

  toggleLanguageDropdown(forceIsExpanded = null) {
    const isExpanded = this.languageToggleTarget.getAttribute('aria-expanded') === 'true';

    const newIsExpanded = forceIsExpanded !== null ? forceIsExpanded : !isExpanded;

    this.languageToggleTarget.setAttribute('aria-expanded', newIsExpanded);
  }

  onWindowKeyUp(event) {
    if (event.keyCode === 27 && this.isExpanded) {
      this.toggleNavigation({ forceIsExpanded: false });
      this.closeDropdowns();
      this.toggleLanguageDropdown(false);
    }
  }

  onWindowClick(event) {
    const { target } = event;

    if (!target.closest('.header') && !target.closest('.dialog')) {
      this.toggleNavigation({ forceIsExpanded: false });
      this.closeDropdowns();
      this.toggleLanguageDropdown(false);
    }
  }

  closeNavigationDropdown() {
    gsap.to(this.navigationTarget, {
      opacity: 0,
      duration: 0.2,
      onComplete: () => {
        this.navigationTarget.style.visibility = 'hidden';
      },
    });
  }

  openNavigationDropdown() {
    gsap.set(this.navigationTarget, {
      visibility: 'visible',
    });

    gsap.to(this.navigationTarget, {
      opacity: 1,
      duration: 0.2,
    });
  }

  setA11yExpandedForToggle(toggle, state = 'true') {
    const dropdown = this.dropdownForToggle(toggle);

    if (dropdown) {
      toggle.setAttribute('aria-expanded', state);
      dropdown.setAttribute('aria-expanded', state);
    }
  }

  closeDropdowns() {
    this.dropdownTargets.forEach((dropdown) => {
      const toggle = document.getElementById(dropdown.getAttribute('aria-controlledby'));

      if (!toggle) {
        throw new Error('Missing toggle');
      }

      toggle.setAttribute('aria-expanded', 'false');

      dropdown.classList.remove('is-visible');
    });
  }

  dropdownForToggle(toggle) {
    const id = toggle.getAttribute('aria-controls');

    return this.navigationTarget.querySelector(`#${id}`);
  }
}

NavigationController.targets = [
  'navigation',
  'navigationToggle',
  'dropdown',
  'languageToggle',
];

export default NavigationController;
